import { Injectable } from '@angular/core';

import { Action, Store } from '@ngrx/store';
import { Actions, Effect, ofType } from '@ngrx/effects';

import { Observable, of, zip } from 'rxjs';
import { switchMap, catchError, mergeMap, withLatestFrom } from 'rxjs/operators';

import { AppState, Selectors } from '../root-reducer';
import {
  RecordActionTypes,
  GetRecords,
  GetRecordsSuccess,
  GetRecordsFailure,
  GetIndividualRecord,
  GetIndividualRecordSuccess,
  GetIndividualRecordFailure,
  GetCorporateRecord,
  GetCorporateRecordSuccess,
  GetCorporateRecordFailure,
  GetPrimaryCrpRecord,
  ClearGetPrimaryCrpRecord,
  AddCrpRole,
  AddCrpRoleFailure,
  AddCrpRoleSuccess,
  DeleteCrp,
  DeleteCrpSuccess,
  DeleteCrpFailure,
  DeleteRole,
  DeleteRoleFailure,
  DeleteRoleSuccess,
  GetCrpRoles,
  GetCrpRolesFailure,
  GetCrpRolesSuccess,
  GetOtherData,
  GetOtherDataSuccess,
  GetOtherDataFailure
} from '../actions/record.action';
import { ReloadCrpList } from 'store/actions/customer.actions';

import { Customer } from 'modules/shared/models/customer.models';
import {
  RecordOtherDataResponse, GetPrimaryCrpRecordPayload, GetRecordOtherDataPayload, GetRecordPayload
} from 'store/models/record.models';
import { Crp } from 'modules/shared/models/crp.models';
import { CrpResponse, CrpPayload } from 'store/models/crp.models';

import { RecordService } from 'modules/core/services/record.service';
import { CrpService } from 'modules/core/services/crp.service';
import { CustomerService } from 'modules/core/services/customer.service';

@Injectable()
export class RecordEffects {
  constructor(private actions$: Actions,
              private store: Store<AppState>,
              private recordService: RecordService,
              private crpService: CrpService,
              private customerService: CustomerService) { }

  @Effect() getRecords$: Observable<Action> = this.actions$.pipe(
    ofType<GetRecords>(RecordActionTypes.GET_RECORDS),
    mergeMap((res: any) => {
      return this.recordService.getRecords(res.payload).pipe(
        switchMap((record: any) => {
          return of(new GetRecordsSuccess(record));
        }),
        catchError(error => {
          return of(new GetRecordsFailure(error.message));
        })
      );
    })
  );

  @Effect() getIndividualRecord$: Observable<Action> = this.actions$.pipe(
    ofType<GetIndividualRecord>(RecordActionTypes.GET_INDIVIDUAL_RECORD),
    withLatestFrom(this.store.select(Selectors.customer.getCustomer)),
    mergeMap(([res, customer]: [GetIndividualRecord, Customer]) => {
      return this.recordService.getIndividualRecord(res.payload.recordId).pipe(
        switchMap((record: any) => {
          const actions$: Array<any> = [];

          if (res.payload && res.payload.getOtherData) {
            if (customer) {
              actions$.push(new GetOtherData(<GetRecordOtherDataPayload>{ record: record.id, customer: customer.id}));
            } else if (res.payload.customerId) {
              actions$.push(new GetOtherData(<GetRecordOtherDataPayload>{ record: record.id, customer: res.payload.customerId }));
            }
          }

          actions$.push(new GetIndividualRecordSuccess(record));

          return actions$;
        }),
        catchError(error => {
          return of(new GetIndividualRecordFailure(error.message));
        })
      );
    })
  );

  @Effect() getCorporateRecord$: Observable<Action> = this.actions$.pipe(
    ofType<GetCorporateRecord>(RecordActionTypes.GET_CORPORATE_RECORD),
    withLatestFrom(this.store.select(Selectors.customer.getCustomer)),
    mergeMap(([res, customer]: [GetCorporateRecord, Customer]) => {
      return this.recordService.getCorporateRecord(res.payload.recordId).pipe(
        switchMap((record: any) => {
          const actions$: Array<any> = [];

          if (res.payload && res.payload.getOtherData) {
            if (customer) {
              actions$.push(new GetOtherData(<GetRecordOtherDataPayload>{ record: record.id, customer: customer.id}));
            } else if (res.payload.customerId) {
              actions$.push(new GetOtherData(<GetRecordOtherDataPayload>{ record: record.id, customer: res.payload.customerId }));
            }
          }

          actions$.push(new GetCorporateRecordSuccess(record));

          return actions$;
        }),
        catchError(error => {
          return of(new GetCorporateRecordFailure(error.message));
        })
      );
    })
  );

  @Effect() GetPrimaryCrpRecord$: Observable<Action> = this.actions$.pipe(
    ofType<GetPrimaryCrpRecord>(RecordActionTypes.GET_PRIMARY_CRP_RECORD),
    withLatestFrom(this.store.select(Selectors.customer.getCustomer)),
    switchMap(([input, customer]: [GetPrimaryCrpRecord, Customer]) => {
      if (customer && customer.id === input.payload.customerId) {
        return zip(of(input.payload), of(customer), this.store.select(Selectors.customer.getCrps));
      } else {
        return zip(
          of(input.payload),
          this.customerService.getCustomer(input.payload.customerId),
          this.store.select(Selectors.customer.getCrps)
        );
      }
    }),
    switchMap(([payload, customer, crps]: [GetPrimaryCrpRecordPayload, Customer, CrpResponse]) => {
      if (crps && crps.count) {
        return zip(of(payload), of(customer), of(crps));
      } else {
        return zip(of(payload), of(customer), this.customerService.getCustomerCrps(<CrpPayload>{customerId: customer.id}));
      }
    }),
    switchMap(([payload, customer, crps]: [GetPrimaryCrpRecordPayload, Customer, CrpResponse]) => {
      const primaryCrp = crps.results.find((crp: Crp) => crp.primary === true);
      if (primaryCrp) {
        const actions$: Array<any> = [];

        if (primaryCrp.recordType === 'INDIVIDUAL') {
          actions$.push(new GetIndividualRecord(<GetRecordPayload>{
            recordId: primaryCrp.record,
            getOtherData: payload.getOtherData
          }));
        } else {
          actions$.push(new GetCorporateRecord(<GetRecordPayload>{
            recordId: primaryCrp.record,
            getOtherData: payload.getOtherData
          }));
        }

        return actions$;
      } else {
        return of(new ClearGetPrimaryCrpRecord());
      }
    })
  );

  @Effect() addCrpRole$: Observable<Action> = this.actions$.pipe(
    ofType<AddCrpRole>(RecordActionTypes.ADD_CRP_ROLE),
    mergeMap((res: any) => {
      return this.crpService.addCrpRole(
        res.payload
      ).pipe(
        switchMap((role: any) => {
          return of(new AddCrpRoleSuccess(role));
        }),
        catchError(error => {
          return of(new AddCrpRoleFailure(error.message));
        })
      );
    })
  );

  @Effect() deleteCrp$: Observable<any> = this.actions$.pipe(
    ofType<DeleteCrp>(RecordActionTypes.DELETE_CRP),
    mergeMap((res: any) => {
      return zip(of(res.payload), this.crpService.deleteCrp(res.payload)).pipe(
        mergeMap(([crpId, deleteResponse]: [any, any]) => {
          return [
            new DeleteCrpSuccess(deleteResponse),
            new ReloadCrpList(deleteResponse)
          ];
        }),
        catchError(error => {
          return of(new DeleteCrpFailure(error.message));
        })
      );
    })
  );

  @Effect() deleteRole$: Observable<Action> = this.actions$.pipe(
    ofType<DeleteRole>(RecordActionTypes.DELETE_ROLE),
    mergeMap((res: any) => {
      return this.crpService.deleteRole(res.payload).pipe(
        switchMap((role: any) => {
          return of(new DeleteRoleSuccess(role));
        }),
        catchError(error => {
          return of(new DeleteRoleFailure(error.message));
        })
      );
    })
  );

  @Effect() getCrpRoles$: Observable<Action> = this.actions$.pipe(
    ofType<GetCrpRoles>(RecordActionTypes.GET_CRP_ROLES),
    mergeMap((res: any) => {
      return this.crpService.getCrpRoles(res.payload).pipe(
        switchMap((role: any) => {
          return of(new GetCrpRolesSuccess(role));
        }),
        catchError(error => {
          return of(new GetCrpRolesFailure(error.message));
        })
      );
    })
  );

  @Effect() getOtherData$: Observable<Action> = this.actions$.pipe(
    ofType<GetOtherData>(RecordActionTypes.GET_OTHER_DATA),
    mergeMap((res: GetOtherData) => {
      return this.recordService.getOtherData(res.payload).pipe(
        switchMap((recordOtherDataResponse: RecordOtherDataResponse) => {
          return of(new GetOtherDataSuccess(recordOtherDataResponse));
        }),
        catchError(error => {
          return of(new GetOtherDataFailure(error.message));
        })
      );
    })
  );
}
